import React, { useEffect, useId, useState } from 'react';
import { Loader2, Plus, Trash2, X } from 'lucide-react';

export interface TeamMemberRow {
  id?: string;
  role: string;
  name?: string;
  title: string;
}

const FIELD_INPUT =
  'w-full rounded-lg border border-[#e5e7eb] bg-white px-3 py-2 text-xs leading-5 text-[#18365E] outline-none transition-colors placeholder:text-[#94a3b8] focus:border-[#18365E]/40 focus:ring-2 focus:ring-[#18365E]/10';

const FIELD_LABEL =
  'block text-[10px] font-semibold uppercase tracking-wide leading-4 text-[#717182]';

const TEAM_ROLES = [
  'Executive Sponsor',
  'Initiative Owner',
  'Project Manager',
  'Clinical Lead',
  'Data / Analytics Lead',
  'Finance Lead',
  'IT / Technical Lead',
  'Change Management Lead',
  'Team Member',
] as const;

const TEAM_TITLES = [
  'Chief Medical Officer',
  'Chief Financial Officer',
  'Chief Information Officer',
  'Chief Data Officer',
  'VP Analytics',
  'VP Operations',
  'Director of Clinical Informatics',
  'Director of Finance',
  'Program Manager',
  'Other',
] as const;

const initials = (value: string) =>
  value
    .split(' ')
    .map((word) => word[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

function FieldLabel({
  children,
  required = false,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <span className={FIELD_LABEL}>
      {children}
      {required && (
        <span className="text-[#FF6E4C] ml-0.5" aria-hidden="true">
          *
        </span>
      )}
      {required && <span className="sr-only"> (required)</span>}
    </span>
  );
}

function AddMemberModal({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave: (member: TeamMemberRow) => void | Promise<void>;
}) {
  const titleId = useId();
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const canSave = name.trim().length > 0 && role.trim().length > 0 && !saving;

  const handleSave = async () => {
    if (name.trim().length === 0 || role.trim().length === 0) {
      setError('Name and role are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave({
        id: `local-${Date.now()}`,
        name: name.trim(),
        role: role.trim(),
        title: title.trim(),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add team member.');
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-white rounded-xl shadow-xl max-w-md w-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e5e7eb]">
          <h3 id={titleId} className="text-sm font-semibold text-[#18365E]">
            Add team member
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="p-1.5 hover:bg-[#F5F7FA] rounded-lg disabled:opacity-40"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-[#717182]" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <label className="block space-y-1.5">
            <FieldLabel required>Name</FieldLabel>
            <input
              autoFocus
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Dr. Sarah Chen"
              className={FIELD_INPUT}
            />
          </label>

          <label className="block space-y-1.5">
            <FieldLabel required>Role</FieldLabel>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              required
              aria-required="true"
              className={FIELD_INPUT}
            >
              <option value="">Select role</option>
              {TEAM_ROLES.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>

          <label className="block space-y-1.5">
            <FieldLabel>Title</FieldLabel>
            <select
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={FIELD_INPUT}
            >
              <option value="">Select title (optional)</option>
              {TEAM_TITLES.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>

          {error && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#e5e7eb] bg-[#FAFBFC] rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg px-3 py-2 text-xs font-semibold text-[#717182] hover:bg-[#F5F7FA] transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSave}
            onClick={() => { void handleSave(); }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#18365E] px-3.5 py-2 text-xs font-semibold text-white hover:bg-[#142c4d] transition-colors disabled:opacity-40 disabled:pointer-events-none"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
            {saving ? 'Adding…' : 'Add member'}
          </button>
        </div>
      </div>
    </div>
  );
}

export interface TeamListProps {
  team: TeamMemberRow[];
  onAdd?: (member: TeamMemberRow) => void | Promise<void>;
  onRemove?: (member: TeamMemberRow, index: number) => void | Promise<void>;
}

export const TeamList: React.FC<TeamListProps> = ({ team, onAdd, onRemove }) => {
  const [adding, setAdding] = useState(false);

  return (
    <div className="space-y-3">
      {team.length === 0 ? (
        <p className="text-xs text-[#717182]">No team members assigned yet.</p>
      ) : (
        <ul className="space-y-2.5">
          {team.map((member, index) => {
            const primary = member.name || member.title || member.role;
            return (
              <li key={member.id ?? `${member.role}-${index}`} className="flex items-center gap-2.5 group">
                <span className="w-7 h-7 rounded-full bg-[#18365E] text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                  {initials(primary)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-[#18365E] leading-tight">{primary}</p>
                  <p className="text-[10px] text-[#717182]">
                    {member.role}
                    {member.name && member.title ? ` · ${member.title}` : ''}
                  </p>
                </div>
                {onRemove && (
                  <button
                    type="button"
                    onClick={() => onRemove(member, index)}
                    className="p-1 rounded-md text-[#94a3b8] hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                    aria-label={`Remove ${primary}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {onAdd && (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#18365E] hover:text-[#FF6E4C] transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add team member
        </button>
      )}

      {adding && onAdd && (
        <AddMemberModal
          onClose={() => setAdding(false)}
          onSave={async (member) => {
            await onAdd(member);
            setAdding(false);
          }}
        />
      )}
    </div>
  );
};

export default TeamList;
